import createClient from 'openapi-fetch'
import type { DefaultOptions } from '@tanstack/react-query'
import type { paths, components } from './schema'
import { responseError } from '@/lib/api-error'

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
const REQUEST_TIMEOUT_MS = 30_000

export const dashboardMutationDefaults: NonNullable<DefaultOptions['mutations']> = {
  retry: false,
}

export class AuthError extends Error {
  status: number
  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'AuthError'
    this.status = status
  }
}

export function createFetchWithTimeout(timeoutMs: number): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const controller = new AbortController()
    const requestSignal = init?.signal
    let timedOut = false
    const abortRequest = () => controller.abort(requestSignal?.reason)

    if (requestSignal?.aborted) abortRequest()
    else requestSignal?.addEventListener('abort', abortRequest, { once: true })

    const timeoutId = setTimeout(() => {
      timedOut = true
      controller.abort()
    }, timeoutMs)

    try {
      const response = await fetch(input, {
        ...init,
        signal: controller.signal,
      })
      if (response.status === 401 || response.status === 403) {
        const body = await response.clone().json().catch(() => ({}))
        throw new AuthError(response.status, body.detail || 'Not authenticated')
      }
      return response
    } catch (error) {
      if (error instanceof AuthError) throw error
      if (
        timedOut &&
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        error.name === 'AbortError'
      ) {
        throw new Error(`Request timed out after ${timeoutMs}ms`)
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
      requestSignal?.removeEventListener('abort', abortRequest)
    }
  }
}

export const apiFetch = createFetchWithTimeout(REQUEST_TIMEOUT_MS)

function authenticatedHeaders(token: string, headers?: HeadersInit, json = true): Headers {
  const result = new Headers(headers)
  result.set('Authorization', `Bearer ${token}`)
  if (json) result.set('Content-Type', 'application/json')
  return result
}

export function createAuthenticatedClient(accessToken: string) {
  return createClient<paths>({
    baseUrl: API_BASE_URL,
    headers: authenticatedHeaders(accessToken),
    fetch: apiFetch,
  })
}

// Invoice policy deliberately differs from OpenAPI: raw fetch (no timeout), a
// readable Error (no AuthError/recovery), JSON only when supplied, and undefined on 204.
export async function invoiceFetch<T>(
  token: string,
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const headers = authenticatedHeaders(token, init.headers, init.json !== undefined)
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
  })

  if (!response.ok) {
    throw await responseError(response, `Invoice API request failed with ${response.status}`)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export type { paths, components }
